import { LOG_STREAM } from '../../catalog/diagnostics';
import { RCU_CMDTYPE, RCU_TOP_TYPE } from '../../catalog/protocol';
import type { Result } from '../../result';
import type { RcuDocument } from '../../wire';
import { fieldRange, validated } from './validate';

/**
 * Everything the console asks the device about itself: its status document, the
 * panel watch, and the log stream.
 */

/** Asks for the status document now; it also arrives retained on MQTT. */
export const readStatus = (): Result<RcuDocument> =>
  validated({ type: RCU_TOP_TYPE.COMMAND, data: { cmdtype: RCU_CMDTYPE.READ_STATUS } });

/**
 * Watches panel operations for the firmware's fixed five-minute window. Coalesced
 * group-3 requests never reach the worker, so a scene pressed while one is
 * already pending produces no event at all.
 */
export const listenPanel = (): Result<RcuDocument> =>
  validated({ type: RCU_TOP_TYPE.COMMAND, data: { cmdtype: RCU_CMDTYPE.LISTEN_PANEL } });

/** Current console and stream levels. `enabled` comes back unreliable. */
export const readLoggingStatus = (): Result<RcuDocument> =>
  validated({ type: RCU_TOP_TYPE.COMMAND, data: { cmdtype: RCU_CMDTYPE.READ_LOGGING_STATUS } });

export interface SetLoggingInput {
  /** False sets the console to ERROR only. */
  readonly enable: boolean;
  /** Omitted leaves MQTT streaming as it is. */
  readonly mqtt?: boolean;
}

export const setLogging = (input: SetLoggingInput): Result<RcuDocument> =>
  validated({
    type: RCU_TOP_TYPE.COMMAND,
    data: {
      cmdtype: RCU_CMDTYPE.SET_LOGGING,
      enable: input.enable,
      ...(input.mqtt === undefined ? {} : { mqtt: input.mqtt }),
    },
  });

/**
 * Rewinds the log ring and has it streamed to the log topic. Zero or less means
 * the firmware's own default; a larger ask is capped at what it keeps in RAM.
 */
export const readLogs = (lines: number = LOG_STREAM.DEFAULT_LINES): Result<RcuDocument> => {
  // The firmware casts, defaults non-positive to 100 and caps at 128, so the
  // console sends the number it means rather than the one it wishes for.
  const requested =
    Number.isInteger(lines) && lines > 0
      ? Math.min(lines, LOG_STREAM.MAX_LINES)
      : LOG_STREAM.DEFAULT_LINES;
  return validated(
    { type: RCU_TOP_TYPE.COMMAND, data: { cmdtype: RCU_CMDTYPE.READ_LOGS, lines: requested } },
    [fieldRange('Lines', requested, LOG_STREAM.MIN_LINES, LOG_STREAM.MAX_LINES)],
  );
};
